import React, { useEffect, useState, useMemo } from "react";
import { api, extractError } from "@/lib/api";
import { Link } from "react-router-dom";
import { formatDateIt } from "@/lib/format";
import { Plus, X, ChevronDown, ChevronRight } from "lucide-react";

const INTERNAL_TYPES = [
    { key: "internal_long", label: "A lungo termine" },
    { key: "internal_weekly", label: "Settimanali" },
];

function classifyStatus(status) {
    if (!status) return "active";
    if (status.is_final) return "concluso";
    if ((status.name || "").toLowerCase().includes("pausa")) return "pausa";
    return "active";
}

export default function InternalProjects() {
    const [list, setList] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [users, setUsers] = useState([]);
    const [metricsByProject, setMetricsByProject] = useState({});
    const [fType, setFType] = useState("");
    const [show, setShow] = useState(false);
    const [expandPausa, setExpandPausa] = useState(false);
    const [expandConcluso, setExpandConcluso] = useState(false);

    const load = async () => {
        // Load all internal projects (both internal_long + internal_weekly),
        // filter client-side because API supports single project_type filter.
        const [a, b] = await Promise.all([
            api.get("/projects", { params: { project_type: "internal_long" } }),
            api.get("/projects", { params: { project_type: "internal_weekly" } }),
        ]);
        const all = [...a.data, ...b.data];
        setList(all);
        const metrics = {};
        await Promise.all(all.map(async (p) => {
            try { metrics[p.id] = (await api.get(`/projects/${p.id}/metrics`)).data; }
            catch (e) { console.error(`Load metrics for ${p.id} failed:`, e); }
        }));
        setMetricsByProject(metrics);
    };
    useEffect(() => {
        (async () => {
            const [s, u] = await Promise.all([
                api.get("/settings/project-statuses"), api.get("/users"),
            ]);
            setStatuses(s.data); setUsers(u.data);
        })();
    }, []);
    useEffect(() => { load(); }, []);

    const statusMap = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);

    const filtered = fType ? list.filter((p) => p.project_type === fType) : list;
    const grouped = useMemo(() => {
        const g = { active: [], pausa: [], concluso: [] };
        filtered.forEach((p) => g[classifyStatus(statusMap[p.status_id])].push(p));
        return g;
    }, [filtered, statusMap]);

    const changeStatus = async (pid, sid) => {
        setList((prev) => prev.map((p) => p.id === pid ? { ...p, status_id: sid } : p));
        try { await api.patch(`/projects/${pid}`, { status_id: sid }); } catch { load(); }
    };

    return (
        <div className="p-6" data-testid="internal-projects-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Progetti interni</h1>
                    <div className="text-sm text-neutral-500">Progetti dello studio · {list.length} totali · <span className="text-neutral-400">non conteggiati nel Report</span></div>
                </div>
                <button onClick={() => setShow(true)} data-testid="new-internal-project-btn"
                    className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                    <Plus size={14} /> Nuovo progetto interno
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-wrap gap-2 mb-4">
                <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="filter-internal-type"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti i tipi</option>
                    {INTERNAL_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
            </div>

            <ProjectsTable projects={grouped.active} statusMap={statusMap} statuses={statuses} metricsByProject={metricsByProject} onStatusChange={changeStatus} testId="internal-active" />

            <div className="mt-3 space-y-2">
                <Accordion title="In pausa" count={grouped.pausa.length} expanded={expandPausa}
                    onToggle={() => setExpandPausa(!expandPausa)} testId="internal-folder-pausa">
                    <ProjectsTable projects={grouped.pausa} statusMap={statusMap} statuses={statuses} metricsByProject={metricsByProject}
                        onStatusChange={changeStatus} testId="internal-pausa" muted />
                </Accordion>
                <Accordion title="Concluso" count={grouped.concluso.length} expanded={expandConcluso}
                    onToggle={() => setExpandConcluso(!expandConcluso)} testId="internal-folder-concluso">
                    <ProjectsTable projects={grouped.concluso} statusMap={statusMap} statuses={statuses} metricsByProject={metricsByProject}
                        onStatusChange={changeStatus} testId="internal-concluso" muted />
                </Accordion>
            </div>

            {show && <NewInternalProjectModal statuses={statuses} users={users} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
        </div>
    );
}

function Accordion({ title, count, expanded, onToggle, testId, children }) {
    return (
        <div className="bg-neutral-100/60 border border-dashed border-neutral-200 rounded-md overflow-hidden" data-testid={testId}>
            <button onClick={onToggle} data-testid={`${testId}-toggle`}
                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-neutral-100">
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-display text-xs font-medium tracking-wide uppercase text-neutral-500">{title} · {count}</span>
            </button>
            {expanded && <div>{children}</div>}
        </div>
    );
}

function ProjectsTable({ projects, statusMap, statuses, metricsByProject, onStatusChange, testId, muted }) {
    if (projects.length === 0) {
        return <div className={`bg-white border ${muted ? "" : "border-neutral-200 rounded-md"} p-6 text-sm text-neutral-400`} data-testid={`${testId}-empty`}>Nessun progetto in questa sezione.</div>;
    }
    return (
        <div className={`${muted ? "opacity-70" : "bg-white border border-neutral-200 rounded-md"} overflow-hidden`} data-testid={testId}>
            <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 border-b border-neutral-100">
                    <tr>
                        <th className="text-left px-4 py-2 font-medium">Titolo</th>
                        <th className="text-left px-4 py-2 font-medium">Tipo</th>
                        <th className="text-left px-4 py-2 font-medium">Stato</th>
                        <th className="text-left px-4 py-2 font-medium">Avanzamento</th>
                        <th className="text-left px-4 py-2 font-medium">Scadenza</th>
                    </tr>
                </thead>
                <tbody>
                    {projects.map((p) => {
                        const pct = metricsByProject?.[p.id]?.project_percentage ?? 0;
                        return (
                        <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`internal-row-${p.id}`}>
                            <td className="px-4 py-2">
                                <Link to={`/progetti/${p.id}?from=internal`} className="font-medium text-neutral-900 hover:underline">{p.title}</Link>
                            </td>
                            <td className="px-4 py-2 text-xs text-neutral-500">
                                {p.project_type === "internal_weekly" ? "Settimanale" : "Lungo termine"}
                            </td>
                            <td className="px-4 py-2">
                                <select value={p.status_id || ""} onChange={(e) => onStatusChange(p.id, e.target.value)}
                                    data-testid={`internal-status-${p.id}`}
                                    className="text-xs border border-neutral-200 rounded px-1.5 py-0.5">
                                    {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </td>
                            <td className="px-4 py-2">
                                <div className="flex items-center gap-2 min-w-[120px]" data-testid={`internal-progress-${p.id}`}>
                                    <div className="w-24 h-1.5 rounded bg-neutral-100 overflow-hidden">
                                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs text-neutral-600 tabular-nums">{pct}%</span>
                                </div>
                            </td>
                            <td className="px-4 py-2 text-xs text-neutral-500">{formatDateIt(p.end_date)}</td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function NewInternalProjectModal({ statuses, users, onClose, onCreated }) {
    const [form, setForm] = useState({
        title: "", description: "", project_type: "internal_weekly",
        status_id: statuses[0]?.id || "", assignee_id: users[0]?.id || "",
        start_date: "", end_date: "",
    });
    const [templateId, setTemplateId] = useState("");
    const [templates, setTemplates] = useState([]);
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/sop-templates", { params: { type: "project" } }).then((r) => setTemplates(r.data)).catch(() => {});
    }, []);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const payload = { ...form, client_id: null };
            if (payload.project_type === "internal_weekly") payload.title = payload.title || "auto";
            const { data } = await api.post("/projects", payload);
            if (templateId) {
                try { await api.post(`/sop-templates/${templateId}/apply/project/${data.id}`); }
                catch (e) { console.error("SOP apply failed:", e); }
            }
            onCreated(data);
        } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="new-internal-project-modal"
                className="bg-white rounded-md shadow-lg w-full max-w-lg">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Nuovo progetto interno</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                    {form.project_type === "internal_weekly" ? (
                        <F label="Titolo" full>
                            <div data-testid="internal-title-auto" className="w-full px-2 py-1.5 border border-dashed border-neutral-300 rounded bg-neutral-50 text-neutral-600">
                                <span className="text-xs text-neutral-500 mr-1">Auto:</span>
                                Piano settimana {(() => {
                                    const now = new Date();
                                    const dow = (now.getDay() + 6) % 7; // Mon=0..Sun=6
                                    const daysSinceThu = (dow - 3 + 7) % 7; // Thu-based
                                    const end = new Date(now); end.setDate(now.getDate() - daysSinceThu + 6);
                                    const dd = String(end.getDate()).padStart(2, "0");
                                    const mm = String(end.getMonth() + 1).padStart(2, "0");
                                    return `${dd}/${mm}/${end.getFullYear()}`;
                                })()}
                            </div>
                        </F>
                    ) : (
                        <F label="Titolo*" full><input required data-testid="internal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></F>
                    )}
                    <F label="Tipo">
                        <select data-testid="internal-project-type" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            {INTERNAL_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </F>
                    <F label="Stato">
                        <select value={form.status_id} onChange={(e) => setForm({ ...form, status_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </F>
                    <F label="Assegnato a">
                        <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                        </select>
                    </F>
                    <F label="Inizio"><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></F>
                    <F label="Fine"><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></F>
                    {templates.length > 0 && (
                        <F label="Applica template SOP (facoltativo)" full>
                            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                                <option value="">— Nessun template —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </F>
                    )}
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-internal-project" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Salvataggio…" : "Crea"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function F({ label, full, children }) {
    return <label className={`block ${full ? "col-span-2" : ""}`}><span className="text-xs font-medium text-neutral-600">{label}</span><div className="mt-1">{children}</div></label>;
}
